import {
	BaseIcon,
	DataList,
	DataListActionsItem,
	DataListClickableRow,
	DataListItem,
	Icon,
	Label,
	useFormatDateTime,
} from "@dgs/core";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventRating } from "../../../../../shared/events/EventRating";
import { ParticipationStatusLabel } from "../../../../../shared/events/eventUsers/ParticipationStatusLabel";
import { EventListOverviewResource } from "../../../../../shared/types/event";

const Heading = styled.div`
	color: ${({ theme }) => theme.colors.palette.black};
	font-size: ${({ theme }) => theme.fonts.sizes.small};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const Text = styled.span`
	color: ${({ theme }) => theme.colors.palette.black};
	font-size: ${({ theme }) => theme.fonts.sizes.small};
	line-height: 1;
`;

const Title = styled.div`
	width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: ${({ theme }) => theme.colors.palette.black};
	font-size: ${({ theme }) => theme.fonts.sizes.normal};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const Duration = styled.div`
	display: grid;
	grid-template-columns: max-content 1fr;
	grid-gap: ${({ theme }) => theme.spacing(1)};
	height: fit-content;
	align-items: center;
`;

const Info = styled.div`
	height: fit-content;
	display: grid;
	grid-template-rows: auto;
	grid-gap: ${({ theme }) => theme.spacing(1)};
`;

const DateIcon = styled(Icon)`
	width: 1.5em;
	height: 1.5em;
	color: ${({ theme }) => theme.colors.palette.grey[80]};
`;

const TimeIcon = styled(Icon)`
	width: 1.5em;
	height: 1.5em;
	color: ${({ theme }) => theme.colors.palette.grey[80]};
`;
const TimeBaseIcon = styled(BaseIcon)`
	width: 1.5em;
	height: 1.5em;
	color: ${({ theme }) => theme.colors.palette.grey[80]};
`;

const Column = styled(DataListItem)`
	align-items: baseline;
	border: none;
	background: ${({ theme }) => theme.colors.palette.content.dark.base};
	margin: ${({ theme }) => theme.spacing(1)} 0;
	padding: ${({ theme }) => theme.spacing(4)};
`;

const FirstColumn = styled(DataListItem)`
	border: none;
	background: ${({ theme }) => theme.colors.palette.content.dark.base};
	margin: ${({ theme }) => theme.spacing(1)} 0;
	padding: 0 ${({ theme }) => theme.spacing(4)} 0 0;
`;

const Row = styled(DataListClickableRow)`
	display: contents;
	cursor: pointer;
`;

const ActionColumn = styled(DataListActionsItem)`
	align-items: baseline;
	border: none;
	background: ${({ theme }) => theme.colors.palette.content.dark.base};
	margin: ${({ theme }) => theme.spacing(1)} 0;
`;

const ImageContainer = styled.div`
	position: relative;
	height: 100%;
	max-width: 300px;
`;

const Image = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const StyledDataList = styled(DataList)`
	grid-template-columns: max-content 1fr 1fr 1fr 1fr;
	overflow-y: hidden;
`;

const Badges = styled.div`
	position: absolute;
	display: flex;
	gap: ${({ theme }) => theme.spacing(2)};
	top: ${({ theme }) => theme.spacing(2)};
	left: ${({ theme }) => theme.spacing(2)};
`;

const HighlightIcon = styled(Icon)`
	height: 16px;
	width: 16px;
`;

interface Props {
	actions?: (event: EventListOverviewResource) => ReactNode;
	onRowClick: (event: EventListOverviewResource) => void;
	events: EventListOverviewResource[];
	isEmpty: boolean;
}

export const OverviewDataList: FC<Props> = ({ events, isEmpty, actions, onRowClick }) => {
	const { t } = useTranslation();
	const formatDateTime = useFormatDateTime();

	return (
		<StyledDataList isEmpty={isEmpty} empty={t("No events available")}>
			{events.map((event) => (
				<Row key={event.id} onClick={() => onRowClick(event)}>
					<FirstColumn>
						<ImageContainer>
							<Image src={event.image.url} alt={event.image.name} />
							<Badges>
								{event.isHighlight && (
									<Label color="primary">
										<HighlightIcon $icon="star_filled" />
										{t("Highlight")}
									</Label>
								)}
								<ParticipationStatusLabel status={event.participationStatus} />
							</Badges>
						</ImageContainer>
					</FirstColumn>
					<Column>
						<Info>
							<Title>{event.name}</Title>
							<Duration>
								<DateIcon $icon="calendar" />
								<Text>
									{event.start && formatDateTime(event.start, { dateStyle: "short" })}
									{" - "}
									{event.end && formatDateTime(event.end, { dateStyle: "short" })}
								</Text>
							</Duration>
							<Duration>
								<TimeIcon $icon="schedule" />
								<Text>
									{event.start && formatDateTime(event.start, { timeStyle: "short" })}
									{" - "}
									{event.end && formatDateTime(event.end, { timeStyle: "short" })}
								</Text>
							</Duration>
							<Duration>
								<TimeBaseIcon src="/timezone.svg" />
								<Text>{event.timezone}</Text>
							</Duration>
						</Info>
					</Column>

					<Column>
						<Info>
							<Heading>{t("Basic information")}</Heading>
							<Text>{event.region ? event.region.name : "-"}</Text>
							<Text>{event.eventType.name}</Text>
						</Info>
					</Column>

					<Column>
						<Info>
							<Heading>{t("Project managers")}</Heading>
							{event.projectManagers.length > 0
								? event.projectManagers.map((user) => (
										<Text key={user.id}>
											{user.name} ({user.email})
										</Text>
									))
								: "-"}
						</Info>
					</Column>
					<ActionColumn>
						<StyledEventRating rating={event.rating} />
						{actions && actions(event)}
					</ActionColumn>
				</Row>
			))}
		</StyledDataList>
	);
};

const StyledEventRating = styled(EventRating)`
	align-self: start;
	margin: 0 ${({ theme }) => theme.spacing(4)};
`;
